import cardClasses from './_card_program.module.scss';
import { generateElementId } from 'music';
/**
 * Music Program Card V3: Render program cards according:
 *    - Single section.
 *    - Multi sections.
 *    - One program.
 *    - Multi program.
 *    - Mobile screen.
 *    - Full screen.
 * 
 * @class
 * 
 * @property {HTMLElement[]} cardActions
 * @property {HTMLElement} cardHeader
 * @property {UaCardProgram} cardProgram
 * @property {HTMLElement[]} cardSections
 * @property {HTMLElement[]} courseContent
 * @property {String} label
 * @property {String} expiration
 * @property {HTMLElement[]} cardSections
 * @property {HTMLElement} programCover
 * @property {HTMLElement[]} sectionContent
 * @property {String} type
 */
export default class UaCardProgram extends HTMLElement {
  cardActions = [];
  cardHeader;
  cardProgram;
  cardSections = [];
  courseContent = [];
  label;
  expiration = null;
  instructorContent = [];
  programCover;
  sectionContent = [];
  type = 'single-program';

  /** 
   * Attributes to watch for changes
   * @return {String[]}
   */
  static get observedAttributes() {
    return ['type', 'label', 'expiration'];
  }

  /** 
   * Object of UI classes to render card template.
   * @return {Object}
   */
  get classes() {
    return {
      actionButton: cardClasses['c-card-program__action-btn'],
      card: cardClasses['c-card-program__card'],
      cardMultiSection: this.multiSections ? cardClasses['c-card-program__card--multi-section'] : '',
      cardActions: cardClasses['c-card-program__card-actions'],
      cardContent: cardClasses['c-card-program__inner'],
      label: cardClasses['c-card-program__label'],
      courseInfo: cardClasses['c-card-program__course-info'],
      expirationInfo: cardClasses['c-card-program__expiration-info'],
      expirationInfoContent: cardClasses['c-card-program__expiration-info-content'],
      header: cardClasses['c-card-program__card-header'],
      mobileContent: cardClasses['c-card-program__mobile-content'],
      multiSections: this.multiSections ? cardClasses['c-card-program__sections']
        : cardClasses['c-card-program__section'],
      instructorInfo: cardClasses['c-card-program__instructor-info'],
      programCover: cardClasses['c-card-program__cover-program'],
      programType:
      this.type === 'multi-program' ? cardClasses['c-card-program__card--multi-program']
          : cardClasses['c-card-program__card--single-program'],
      sectionsContent: cardClasses['c-card-program__sections-content'],
      sectionInfo: cardClasses['c-card-program__section-info']
    };
  }

  /** 
   * Object of templates to render according multi/single section variation.
   * @return {Object}
   */
  get templates() {
    return {
      fullscreenContent: this.multiSections ? this.multiSectionTemplate : this.cardInfoTemplate(0),
      mobileContent: this.multiSections ? '' : this.cardMobileInfoTemplate(0),
      mobileFooter: this.multiSections ? this.multiFooterMobileTemplate : this.singleFooterMobileTemplate,
    }
  }

  /** 
   * Returns layout value to render music-card-v3 component according screensize
   * @return {String}
   */
  get cardLayout() {
    return this.mobile ? 'stack' : 'semi-line-rt';
  }

  /** 
   * Returns card template according cardTemplate value which depends on mobile/full screen
   * @return {String}
   */
  get template() {
    return `<music-card-v3
        aria-labelledby="card-header-${this.id}"
        role="region"
        class="${this.classes.card}  ${this.classes.programType}  ${this.classes.cardMultiSection}"
        layout="${this.cardLayout}">
      ${this.labelTemplate}
      ${this.cardTemplate}  
    </music-card-v3>`
  }

  /** 
   * Returns card template for full screen
   * @return {String}
   */
  get templateFullScreen() {
    return `<div slot="card-media" class="${this.classes.programCover}">
          ${this.programCover.innerHTML}
        </div>
        <div slot="card-content" class="${this.classes.cardContent}">
          ${this.headerTemplate}
          ${this.templates.fullscreenContent}
        </div>`
  }

  /** 
   * Returns card template for mobile screen
   * @return {String}
   */
  get templateMobile() {
    return `<l-spanner-v3  slot="card-media">
      <div class="${this.classes.programCover}">
        ${this.programCover.innerHTML}
      </div>
      <l-stack-v3 gap="2" class="${this.classes.mobileContent}">
        ${this.headerTemplate}
        ${this.templates.mobileContent}
        ${this.expirationTemplate}
      </l-stack-v3>
    </l-spanner-v3>
    <div slot="card-content" class="${this.classes.cardContent}">
      ${this.templates.mobileFooter}
    </div>`;
  }

  /** 
   * Returns markup for mutisection when it is fullscreen 
   * @return {String}
   */
  get multiSectionTemplate() {
   return `<ul class="${this.classes.sectionsContent}  c-scrollbar-v3">
        ${this.cardSections.map((section, idx) =>
          `<li class="${this.classes.multiSections}">
            ${this.cardInfoTemplate(idx)}
          </li>`
        ).join('')}
      </ul>`;
  }

  /** 
   * Returns markup for mutisection when it is mobile 
   * @return {String}
   */
  get multiFooterMobileTemplate() {
    return `<ul class="${this.classes.sectionsContent}  c-scrollbar-v3">
        ${this.cardSections.map((section, idx) =>
          `<li class="${this.classes.multiSections}">
            ${this.cardMobileInfoTemplate(idx)}
            ${this.getActionsTemplate(idx)}
          </li>`
        ).join('')}
      </ul>`;
  }

  /** 
   * Returns markup for single section when it is mobile 
   * @return {String}
   */
  get singleFooterMobileTemplate() {
    return `${this.getActionsTemplate(0)}`;
  }

  /** 
   * Returns markup card info: course, section, expiration, instructor and actions.
   * for fullscreen.
   * @param {Number} idx - Section index
   * @return {String}
   */
  cardInfoTemplate(idx) {
    return `
      ${this.expirationTemplate}  
      <div>
        ${this.courseContent[idx] ? `<p id="course-${this.id}-${idx}" class="${this.classes.courseInfo}">
          ${this.courseContent[idx].innerHTML}
        </p>`: '' }
        ${this.sectionContent[idx] ? `<p id="section-${this.id}-${idx}" class="${this.classes.sectionInfo}">
          ${this.sectionContent[idx].innerHTML}
        </p>`: '' }
      </div>

    ${this.instructorContent[idx] ? `<p class="${this.classes.instructorInfo}">
      ${this.instructorContent[idx].innerHTML}
    </p>`: ''}
    ${this.getActionsTemplate(idx)}`
  }

  /** 
   * Returns markup card info: course, section, expiration, instructor and actions.
   * for mobile screen.
   * @param {Number} idx - Section index
   * @return {String}
   */
  cardMobileInfoTemplate(idx) {
    return `<l-stack-v3 gap="2">
        ${this.courseContent[idx] ? `<p class="${this.classes.courseInfo}">
          ${this.courseContent[idx].innerHTML}
        </p>`: '' }
        ${this.sectionContent[idx] ? `<p class="${this.classes.sectionInfo}">
          ${this.sectionContent[idx].innerHTML}
        </p>`: '' }
        ${this.instructorContent[idx] ? `<p class="${this.classes.instructorInfo}">
          ${this.instructorContent[idx].innerHTML}
        </p>`: '' }
    </l-stack-v3>`
  }

  /** 
   * Returns markup for actions
   * @param {Number} idx - Section index
   * @return {String}
   */
  getActionsTemplate(idx) {
    return `<l-stack-v3 align-x="center" slot="card-actions" class="${this.classes.cardActions}">
        ${this.cardActions[idx].innerHTML}
      </l-stack-v3>`;
  }

  /** 
   * Returns template to render according screen size
   * @return {String}
   */
  get cardTemplate() {
    return this.mobile ? this.templateMobile : this.templateFullScreen;
  }

  /** 
   * Returns header template
   * @return {String}
   */
  get headerTemplate() {
    if(this.cardHeaderMain) {
      return `<l-stack-v3 gap="xs" class="u-mar-bot-12">
        <l-line gap="2" align-y="baseline" class="u-flex-wrap">
          <h2 id="card-header-${this.id}" slot="card-header" class="${this.classes.header}">
          ${this.cardHeaderMain.innerHTML}
          </h2>
          ${this.cardHeaderEdition ? `<span id="edition-${this.id}" class="u-txt-body-2">${this.cardHeaderEdition.innerHTML}</span>` : ''}
        </l-line>
        ${this.cardHeaderSub ? `<p id="headersub-${this.id}" class="u-txt-body-1">${this.cardHeaderSub.innerHTML}</p>` : ''}
      </l-stack-v3>`
    }
    return `<h2 id="card-header-${this.id}" slot="card-header" class="${this.classes.header}">
        ${this.cardHeader.innerHTML}
      </h2>`
  }

  /** 
   * Returns expiration template
   * @return {String}
   */
  get expirationTemplate() {
    return this.expiration ?
      `<div class="${this.classes.expirationInfoContent}">
        <sl-badge variant="warning" pill class="${this.classes.expirationInfo}">${this.expiration}</sl-badge>
      </div>`
      : '';
  }

  /** 
   * Returns label template
   * @return {String}
   */
  get labelTemplate() {
    return this.label ?
      `<div class="${this.classes.label}">${this.label}</div>` : '';
  }

  /** 
   * Returns true when card has more than one section
   * @return {Boolean}
   */
  get multiSections() {
    return  this.cardSections.length > 1;
  }

  /** 
   * Returns true when screen is mobile
   * @return {Boolean}
   */
  get mobile() {
    return screen.width < 700;
  }

  connectedCallback() {
    this.id = generateElementId('card');
    this.getCardElms();
    this.decorate();
    this.handleEvents();
    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', () => this.render());
  }

  /** 
   * Render card template
   */
  render() {
    if (this.cardProgram) {
      this.innerHTML = this.template;
    }
  }

  /** 
   * Gets card elements to populate card program component
   */
  getCardElms() {
    this.cardProgram = this;
    this.programCover = this.querySelector('[slot="card-program-cover"]');
    this.cardHeader = this.querySelector('[slot="card-program-header"]');
    this.cardHeaderMain = this.querySelector('[slot="card-program-header-main"]');
    this.cardHeaderSub = this.querySelector('[slot="card-program-header-sub"]');
    this.cardHeaderEdition = this.querySelector('[slot="card-program-header-edition"]');
    this.getSectionInfoElm();
  }

  /** 
   * Gets info elements: sections, course name, section name, instructor name, card actions.
   */
  getSectionInfoElm() {
    this.cardSections = Array.from(this.querySelectorAll('[slot="card-program-content"]'));

    this.cardSections.forEach((cardSection) => {
      this.courseContent.push(cardSection.querySelector('[slot="card-program-course-info"]'));
      this.sectionContent.push(cardSection.querySelector('[slot="card-program-section-info"]'));
      this.instructorContent.push(cardSection.querySelector('[slot="card-program-instructor-info"]'));
      this.cardActions.push(cardSection.querySelector('[slot="card-program-actions"]'));
    });
  }

  /** 
   * Adds classes to stylish elements
   */
  decorate() {
    this.cardActions.forEach((cardAction, idx) => { 
    const buttons = cardAction.querySelectorAll('button, a');
      buttons.forEach((button) => {
        const assistiveRefSection = this.sectionContent[idx] ? `section-${this.id}-${idx}`: '';
        const assistiveRefCourse = this.courseContent[idx] ? `course-${this.id}-${idx}` : '';
        const assistiveRefEdition = this.cardHeaderEdition ? `edition-${this.id}`: '';
        const assistiveRefHeadersub = this.cardHeaderSub ? `headersub-${this.id}` : '';
        const assistiveRef = `card-header-${this.id} ${assistiveRefEdition} ${assistiveRefHeadersub} ${assistiveRefCourse} ${assistiveRefSection}`.trim();
        button.classList.add(this.classes.actionButton);
        button
          .setAttribute('aria-describedby', assistiveRef);
      });
    });
  }

  /** 
   * Adds events
   */
  handleEvents() {
    window.addEventListener('resize', () => this.render());
  }

  /**
   * Handle attribute value changes.
   *
   * @param {String} attrName - name of the attribute that has changed
   * @param {String} oldValue - previous value of the changed attribute
   * @param {String} newValue - new value of the changed attribute
   */
  attributeChangedCallback(attrName, oldValue, newValue) {
    if (newValue !== oldValue) {
      /** Value attributes */
      this[attrName] = newValue;
      this.render();
    } 
  }
}
